import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { pb } from '../lib/pocketbase.js';
import { useAuth } from '../auth/AuthContext.jsx';
import PraxisFormModal from '../components/PraxisFormModal.jsx';
import KontaktFormModal from '../components/KontaktFormModal.jsx';
import DokumentFormModal from '../components/DokumentFormModal.jsx';
import VertragFormModal from '../components/VertragFormModal.jsx';
import AktivitaetStatusBadge from '../components/AktivitaetStatusBadge.jsx';
import TelefonLink from '../components/TelefonLink.jsx';
import AnrufAmpel from '../components/AnrufAmpel.jsx';
import WiedervorlageMarkierung from '../components/WiedervorlageMarkierung.jsx';
import {
  useAktivitaetChangeListener,
  useAktivitaetModal,
} from '../components/AktivitaetModalProvider.jsx';
import {
  GESPRAECH_ERGEBNIS_BADGE_CLASS,
  GESPRAECH_ERGEBNIS_SHORT_LABEL,
  TYP_ICON,
  TYP_LABEL,
  formatDatum,
  formatDatumZeit,
  heuteAlsInputValue,
  istUeberfaellig,
  toDateInputValue,
  userAnzeige,
} from '../lib/aktivitaet.js';
import {
  PRAXIS_ART_BADGE,
  PRAXIS_ART_SHORT_LABEL,
  praxisArtOrDefault,
  PRAXIS_STATUS_BADGE,
  PRAXIS_STATUS_LABEL,
  praxisStatusOrDefault,
  UNTERNEHMENSFORM_BADGE,
  UNTERNEHMENSFORM_LABEL,
  unternehmensformOrDefault,
  VERSORGUNGSFORM_BADGE,
  VERSORGUNGSFORM_SHORT_LABEL,
  versorgungsformAnzeige,
  SOFTWARE_LABEL,
  ABRECHNUNGSSTELLE_LABEL,
  fetchFilialenCount,
  praxisAnzeige,
  verbundAnzeige,
  hatVertrag,
  PRAXIS_VERTRAG_PARTNER_LABEL,
  PRAXIS_VERTRAG_STATUS_LABEL,
  PRAXIS_VERTRAG_STATUS_BADGE_PILL,
} from '../lib/praxis.js';
import {
  DOKUMENT_TYP_BADGE,
  DOKUMENT_TYP_LABEL,
} from '../lib/dokument.js';
import { dateiendung, formatDateigroesse, formatWebsiteHref } from '../lib/format.js';
import { markPraxisVisited } from '../lib/recentPraxen.js';
import { formatKontaktName, rolleAnzeige } from '../lib/kontakt.js';
import { fachgebietAnzeige } from '../lib/fachrichtung.js';

const TYP_LABEL_PRAXIS = { praxis: 'Arztpraxis', klinik: 'Klinik' };

function StatusPill({ status }) {
  const value = praxisStatusOrDefault(status);
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${
        PRAXIS_STATUS_BADGE[value] ?? 'bg-slate-100 text-slate-700 ring-slate-200'
      }`}
    >
      {PRAXIS_STATUS_LABEL[value] ?? value}
    </span>
  );
}

function PraxisArtPill({ praxisArt }) {
  const value = praxisArtOrDefault(praxisArt);
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${
        PRAXIS_ART_BADGE[value] ?? 'bg-slate-100 text-slate-700 ring-slate-200'
      }`}
    >
      {PRAXIS_ART_SHORT_LABEL[value] ?? value}
    </span>
  );
}

function UnternehmensformPill({ unternehmensform }) {
  const value = unternehmensformOrDefault(unternehmensform);
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${
        UNTERNEHMENSFORM_BADGE[value] ?? 'bg-slate-100 text-slate-700 ring-slate-200'
      }`}
    >
      {UNTERNEHMENSFORM_LABEL[value] ?? value}
    </span>
  );
}

// Leere Versorgungsform bekommt bewusst KEINEN Pill — anders als bei
// praxis_art/unternehmensform gibt es hier keinen "unbekannt"-Default, auf
// den man zurückfallen könnte. Der Wert steht dann nur im Stammdaten-Block.
function VersorgungsformPill({ versorgungsform }) {
  if (!versorgungsform) return null;
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${
        VERSORGUNGSFORM_BADGE[versorgungsform] ?? 'bg-slate-100 text-slate-700 ring-slate-200'
      }`}
    >
      {VERSORGUNGSFORM_SHORT_LABEL[versorgungsform] ?? versorgungsform}
    </span>
  );
}

// Ohne Verbund wird gar nichts gerendert — "Kein Verbund" ist der Normalfall
// und würde die Kopfzeile bei fast jeder Praxis mit einer Nicht-Information
// vollstellen.
function VerbundPill({ praxis }) {
  const anzeige = verbundAnzeige(praxis);
  if (!anzeige) return null;
  return (
    <span className="inline-flex items-center rounded-full bg-violet-100 px-2.5 py-1 text-xs font-medium text-violet-800 ring-1 ring-inset ring-violet-200">
      Verbund: {anzeige}
    </span>
  );
}

function StammdatenBlock({ praxis }) {
  const adresse = [praxis.strasse, [praxis.plz, praxis.ort].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');

  const softwareListe = Array.isArray(praxis.software) ? praxis.software : [];
  const hatSoftware = softwareListe.length > 0;
  const abrechnungLabel = praxis.abrechnungsstelle
    ? ABRECHNUNGSSTELLE_LABEL[praxis.abrechnungsstelle] ?? praxis.abrechnungsstelle
    : null;

  return (
    <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <Field label="Typ">{TYP_LABEL_PRAXIS[praxis.typ] ?? praxis.typ}</Field>
      <Field label="Versorgungsform">{versorgungsformAnzeige(praxis.versorgungsform)}</Field>
      <Field label="Fachgebiet">{fachgebietAnzeige(praxis) || '—'}</Field>
      <Field label="Adresse">{adresse || '—'}</Field>
      <Field label="Telefon">
        <TelefonLink phone={praxis.telefon} praxisId={praxis.id} />
      </Field>
      <Field label="E-Mail">
        {praxis.email ? (
          <a href={`mailto:${praxis.email}`} className="text-primary-700 hover:underline">
            {praxis.email}
          </a>
        ) : (
          '—'
        )}
      </Field>
      <Field label="Website">
        {praxis.website ? (
          <a
            href={formatWebsiteHref(praxis.website)}
            target="_blank"
            rel="noreferrer"
            className="text-primary-700 hover:underline"
          >
            {praxis.website}
          </a>
        ) : (
          '—'
        )}
      </Field>
      <Field label="Praxissoftware" full>
        {hatSoftware ? (
          <div className="flex flex-wrap gap-1.5">
            {softwareListe.map((s) => (
              <span
                key={s}
                className="inline-flex items-center rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-800 ring-1 ring-inset ring-indigo-200"
              >
                {s === 'sonstiges' && praxis.software_sonstiges
                  ? `Sonstiges: ${praxis.software_sonstiges}`
                  : SOFTWARE_LABEL[s] ?? s}
              </span>
            ))}
          </div>
        ) : (
          '—'
        )}
      </Field>
      <Field label="Abrechnungsstelle" full>
        {abrechnungLabel ? (
          praxis.abrechnungsstelle === 'sonstiges' && praxis.abrechnungsstelle_sonstiges
            ? `Sonstiges: ${praxis.abrechnungsstelle_sonstiges}`
            : abrechnungLabel
        ) : (
          '—'
        )}
      </Field>
    </dl>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? 'sm:col-span-2' : ''}>
      <dt className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</dt>
      <dd className="mt-1 text-sm text-slate-900">{children}</dd>
    </div>
  );
}

// PB liefert created/updated als ISO-Timestamp — formatDatum() blendet die
// Uhrzeit aus, hier wollen wir Datum + Uhrzeit zusammen.
function formatDatumZeitVoll(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// Inline-Notiz unter dem Aktivitäts-Titel: standardmäßig auf 2 Zeilen
// geklemmt, mit "mehr anzeigen"-Toggle wenn der Text tatsächlich länger
// rendert. Erkennung über scrollHeight vs. clientHeight, weil rein
// per CSS line-clamp keine Aussage darüber möglich ist.
function NotizInline({ text }) {
  const [istErweitert, setIstErweitert] = useState(false);
  const [istGeklemmt, setIstGeklemmt] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    setIstGeklemmt(el.scrollHeight > el.clientHeight + 1);
  }, [text]);

  if (!text) return null;

  return (
    <div className="mt-1 text-sm leading-relaxed text-slate-700">
      <p
        ref={ref}
        className={`whitespace-pre-line ${istErweitert ? '' : 'line-clamp-2'}`}
      >
        📝 {text}
      </p>
      {(istGeklemmt || istErweitert) && (
        <button
          type="button"
          onClick={() => setIstErweitert((v) => !v)}
          className="mt-0.5 text-xs font-medium text-primary-600 hover:text-primary-800"
        >
          {istErweitert ? 'weniger' : '... mehr anzeigen'}
        </button>
      )}
    </div>
  );
}

// Read-only Tag, das pro Eintrag im "Alle Filialen"-Tab anzeigt, von welchem
// Standort der Datensatz stammt. Die Hauptpraxis bekommt ein anderes
// Styling als die Filialen.
function StandortTag({ name, istHauptpraxis, onClick }) {
  const klassen = istHauptpraxis
    ? 'bg-primary-100 text-primary-800 ring-primary-200'
    : 'bg-sky-100 text-sky-800 ring-sky-200';
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex max-w-[16rem] items-center gap-1 truncate rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset hover:underline ${klassen}`}
      title={`Zu "${name}" wechseln`}
    >
      <span aria-hidden="true">{istHauptpraxis ? '⌂' : '↳'}</span>
      <span className="truncate">{name}</span>
    </button>
  );
}

function FilialenTab({ hauptpraxis, filialen, aktivitaeten, kontakte, loading, onNavigate }) {
  // Lookup-Map id → Anzeigename, damit wir aus jedem Datensatz schnell den
  // Standort-Tag rendern können, ohne über expand zu gehen.
  const standortById = {
    [hauptpraxis.id]: { name: hauptpraxis.name, istHauptpraxis: true },
  };
  filialen.forEach((f) => {
    standortById[f.id] = { name: f.name, istHauptpraxis: false };
  });

  if (loading) {
    return <div className="pt-5 text-sm text-slate-500">Lade Filial-Daten …</div>;
  }

  return (
    <div className="pt-5 space-y-8">
      <div className="rounded-md bg-sky-50 px-4 py-3 text-sm text-sky-900 ring-1 ring-sky-200">
        Konsolidierte Ansicht aller Standorte ({1 + filialen.length}: Hauptpraxis +{' '}
        {filialen.length} {filialen.length === 1 ? 'Filiale' : 'Filialen'}). Read-only —
        Bearbeitung erfolgt am jeweiligen Standort.
      </div>

      <section>
        <h2 className="mb-3 text-base font-semibold text-slate-900">
          Aktivitäten ({aktivitaeten.length})
        </h2>
        {aktivitaeten.length === 0 ? (
          <p className="text-sm text-slate-500">Keine Aktivitäten an den Standorten.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Standort
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Typ
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Titel
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Status
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Fällig
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Zuständig
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white">
                {aktivitaeten.map((a) => {
                  const standort = standortById[a.praxis] ?? {
                    name: a.expand?.praxis?.name ?? '—',
                    istHauptpraxis: false,
                  };
                  return (
                    <tr key={a.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <StandortTag
                          name={standort.name}
                          istHauptpraxis={standort.istHauptpraxis}
                          onClick={() => onNavigate(a.praxis)}
                        />
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700">
                        {TYP_ICON[a.typ] ?? '•'} {TYP_LABEL[a.typ] ?? a.typ}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{a.titel}</div>
                        {a.expand?.kontakt && (
                          <div className="text-xs text-slate-500">
                            mit {formatKontaktName(a.expand.kontakt)}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <AktivitaetStatusBadge status={a.status} />
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {formatDatumZeit(a.faellig_am, a.uhrzeit)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {userAnzeige(a.expand?.zustaendig)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section>
        <h2 className="mb-3 text-base font-semibold text-slate-900">
          Kontakte ({kontakte.length})
        </h2>
        {kontakte.length === 0 ? (
          <p className="text-sm text-slate-500">Keine Kontakte an den Standorten.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Standort
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Name
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Rolle
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    E-Mail
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Telefon
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white">
                {kontakte.map((k) => {
                  const standort = standortById[k.praxis] ?? {
                    name: k.expand?.praxis?.name ?? '—',
                    istHauptpraxis: false,
                  };
                  return (
                    <tr key={k.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <StandortTag
                          name={standort.name}
                          istHauptpraxis={standort.istHauptpraxis}
                          onClick={() => onNavigate(k.praxis)}
                        />
                      </td>
                      <td className="px-4 py-3 font-medium text-slate-900">
                        {formatKontaktName(k)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {rolleAnzeige(k.rolle)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {k.email ? (
                          <a
                            href={`mailto:${k.email}`}
                            className="text-primary-700 hover:underline"
                          >
                            {k.email}
                          </a>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {k.mobil || k.telefon || '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function VertragStatusPill({ status }) {
  const label = PRAXIS_VERTRAG_STATUS_LABEL[status] ?? status;
  const klassen =
    PRAXIS_VERTRAG_STATUS_BADGE_PILL[status] ?? 'bg-slate-100 text-slate-700 ring-slate-200';
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${klassen}`}
    >
      {label}
    </span>
  );
}

function formatProzent(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return `${n.toFixed(2)} %`;
}

function formatEuro(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return `${n.toFixed(2)} €`;
}

function VertragCard({ praxis, onEdit }) {
  const partnerLabel = praxis.vertrag_partner
    ? PRAXIS_VERTRAG_PARTNER_LABEL[praxis.vertrag_partner] ?? praxis.vertrag_partner
    : null;
  const istLeer = !hatVertrag(praxis);

  if (istLeer) {
    return (
      <div className="card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Factoring-Vertrag</h2>
            <p className="mt-1 text-sm text-slate-500">Kein Vertrag erfasst.</p>
          </div>
          <button type="button" className="btn-primary" onClick={onEdit}>
            Vertrag erfassen
          </button>
        </div>
      </div>
    );
  }

  // Filled-State: nur die Felder anzeigen, die tatsächlich gepflegt sind.
  const zeitraumZeilen = [
    { label: 'Versendet', value: praxis.vertrag_versendet_am },
    { label: 'Beginn', value: praxis.vertrag_beginn_am },
    { label: 'Ende', value: praxis.vertrag_ende_am },
  ].filter((z) => z.value);

  const konditionenZeilen = [
    {
      label: 'medifin-Gebühr',
      value: formatProzent(praxis.vertrag_medifin_gebuehr_prozent),
    },
    {
      label: 'Factoring-Gebühr',
      value: formatProzent(praxis.vertrag_factoring_gebuehr_prozent),
    },
    {
      label: 'Grundgebühr / Rechnung',
      value: formatEuro(praxis.vertrag_grundgebuehr_eur),
    },
  ].filter((z) => z.value);

  const hatZeitraum = zeitraumZeilen.length > 0;
  const hatKonditionen = konditionenZeilen.length > 0;
  const nurKopf = !hatZeitraum && !hatKonditionen && !praxis.vertrag_kundennummer;

  return (
    <div className="card space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-2">
          <h2 className="text-base font-semibold text-slate-900">Factoring-Vertrag</h2>
          <div className="flex flex-wrap items-center gap-2">
            {partnerLabel && (
              <span className="text-sm font-medium text-slate-700">{partnerLabel}</span>
            )}
            {praxis.vertrag_status && <VertragStatusPill status={praxis.vertrag_status} />}
          </div>
          {praxis.vertrag_kundennummer && (
            <div className="text-sm text-slate-600">
              Kunden-Nr.: <span className="font-medium">{praxis.vertrag_kundennummer}</span>
            </div>
          )}
        </div>
        <button type="button" className="btn-secondary" onClick={onEdit}>
          Bearbeiten
        </button>
      </div>

      {(hatZeitraum || hatKonditionen) && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {hatZeitraum && (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Zeitraum
              </div>
              <dl className="mt-2 space-y-1 text-sm">
                {zeitraumZeilen.map((z) => (
                  <div key={z.label} className="flex justify-between gap-2">
                    <dt className="text-slate-500">{z.label}</dt>
                    <dd className="text-slate-800">{formatDatum(z.value)}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          {hatKonditionen && (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Konditionen
              </div>
              <dl className="mt-2 space-y-1 text-sm">
                {konditionenZeilen.map((z) => (
                  <div key={z.label} className="flex justify-between gap-2">
                    <dt className="text-slate-500">{z.label}</dt>
                    <dd className="text-slate-800">{z.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </div>
      )}

      {nurKopf && (
        <p className="text-xs text-slate-500">
          Zeitraum und Konditionen sind noch nicht erfasst.
        </p>
      )}
    </div>
  );
}

function TabButton({ active, onClick, children, badge }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative -mb-px border-b-2 px-1 py-3 text-sm font-medium transition ${
        active
          ? 'border-primary-600 text-primary-700'
          : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'
      }`}
    >
      {children}
      {typeof badge === 'number' && (
        <span className="ml-2 inline-flex items-center rounded-full bg-primary-100 px-2 text-xs font-medium text-primary-800">
          {badge}
        </span>
      )}
    </button>
  );
}

export default function PraxisDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const aktivitaetModal = useAktivitaetModal();
  const { user, isAdmin, isFuehrungskraftOrAdmin } = useAuth();

  const selektionId = searchParams.get('selektion') || null;

  const [praxis, setPraxis] = useState(null);
  const [kontakte, setKontakte] = useState([]);
  const [aktivitaeten, setAktivitaeten] = useState([]);
  const [dokumente, setDokumente] = useState([]);
  const [filialen, setFilialen] = useState([]);
  const [selektionKontext, setSelektionKontext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Aktivitäten sind das wichtigste Arbeitsthema beim Öffnen einer Praxis,
  // deshalb sind sie der Default-Tab.
  const [tab, setTab] = useState('aktivitaeten');

  const [praxisModalOpen, setPraxisModalOpen] = useState(false);
  const [kontaktModalOpen, setKontaktModalOpen] = useState(false);
  const [editKontakt, setEditKontakt] = useState(null);
  const [dokumentModalOpen, setDokumentModalOpen] = useState(false);
  const [vertragModalOpen, setVertragModalOpen] = useState(false);

  // Konsolidierte Daten für Tab "Alle Filialen" — lazy nachgeladen,
  // sobald der User das erste Mal auf den Tab klickt.
  const [filialenAktivitaeten, setFilialenAktivitaeten] = useState([]);
  const [filialenKontakte, setFilialenKontakte] = useState([]);
  const [filialenAggregLoading, setFilialenAggregLoading] = useState(false);
  const [filialenAggregGeladen, setFilialenAggregGeladen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [praxisData, kontakteData, aktivitaetenData, dokumenteData, filialenData] =
        await Promise.all([
          pb
            .collection('praxen')
            .getOne(id, { expand: 'partner,angelegt_von,mutter_praxis' }),
          pb.collection('kontakte').getFullList({
            filter: `praxis = "${id}"`,
            sort: 'nachname,vorname',
            expand: 'angelegt_von',
          }),
          pb.collection('aktivitaeten').getFullList({
            filter: `praxis = "${id}"`,
            sort: '-faellig_am,-uhrzeit,-created',
            // vorgaenger_aktivitaet wird mit-expanded, damit die "Folgt auf"-
            // Zeile bei Wiedervorlagen den Originaltitel zeigen kann, auch wenn
            // der Vorgänger schon weiter unten in der Tabelle stünde.
            expand: 'zustaendig,kontakt,erstellt_von,vorgaenger_aktivitaet',
          }),
          pb.collection('dokumente').getFullList({
            filter: `praxis = "${id}"`,
            sort: '-created',
          }),
          // Filialen dieser Praxis. Leer, wenn die Praxis selbst eine Filiale
          // oder eine Einzelpraxis ist.
          pb.collection('praxen').getFullList({
            filter: pb.filter('mutter_praxis = {:id}', { id }),
            sort: 'name',
          }),
        ]);
      setPraxis(praxisData);
      setKontakte(kontakteData);
      setAktivitaeten(aktivitaetenData);
      setDokumente(dokumenteData);
      setFilialen(filialenData);
      markPraxisVisited(praxisData?.id);
    } catch (err) {
      setError(err?.message || 'Daten konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  // Wenn das globale Aktivitäts-Modal etwas speichert (z. B. nach Klick auf
  // einen Telefon-Link), Daten neu laden.
  useAktivitaetChangeListener(load);

  // Beim Wechsel der Praxis (anderer Detail-Aufruf) den Aggregations-Cache
  // verwerfen — sonst zeigt der Tab Daten der vorigen Praxis.
  useEffect(() => {
    setFilialenAggregGeladen(false);
    setFilialenAktivitaeten([]);
    setFilialenKontakte([]);
  }, [id]);

  // Lazy-Load für Tab "Alle Filialen": konsolidierte Aktivitäten & Kontakte
  // aus Hauptpraxis + allen Filialen. Wird nur ein einziges Mal pro Praxis
  // geladen — Modifikationen passieren am jeweiligen Standort, hier nur
  // read-only Anzeige.
  useEffect(() => {
    if (tab !== 'filialen' || filialenAggregGeladen || filialen.length === 0) {
      return;
    }
    let cancelled = false;
    const alleIds = [id, ...filialen.map((f) => f.id)];
    // pb.filter eskapiert jeden Wert einzeln → kein Filter-Injection-Risiko.
    const standortFilter = alleIds
      .map((praxisId) => pb.filter('praxis = {:p}', { p: praxisId }))
      .join(' || ');
    setFilialenAggregLoading(true);
    Promise.all([
      pb.collection('aktivitaeten').getFullList({
        filter: standortFilter,
        sort: '-faellig_am,-uhrzeit,-created',
        expand: 'zustaendig,kontakt,erstellt_von,praxis',
      }),
      pb.collection('kontakte').getFullList({
        filter: standortFilter,
        sort: 'nachname,vorname',
        expand: 'praxis',
      }),
    ])
      .then(([aktData, kontData]) => {
        if (cancelled) return;
        setFilialenAktivitaeten(aktData);
        setFilialenKontakte(kontData);
        setFilialenAggregGeladen(true);
      })
      .catch(() => {
        // Bei Fehler kein Cache setzen, damit der nächste Tab-Klick neu lädt.
      })
      .finally(() => {
        if (!cancelled) setFilialenAggregLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tab, filialenAggregGeladen, filialen, id]);

  // Selektions-Kontext (Banner-Anzeige): minimaler Lazy-Load nur, wenn die
  // URL den Parameter mitgibt. Schlägt der Fetch fehl (z. B. archivierte
  // Selektion), bleibt der Kontext leer und der Banner wird nicht gezeigt.
  useEffect(() => {
    if (!selektionId) {
      setSelektionKontext(null);
      return;
    }
    let abgebrochen = false;
    pb.collection('selektionen')
      .getOne(selektionId, { fields: 'id,name' })
      .then((sel) => {
        if (!abgebrochen) setSelektionKontext(sel);
      })
      .catch(() => {
        if (!abgebrochen) setSelektionKontext(null);
      });
    return () => {
      abgebrochen = true;
    };
  }, [selektionId]);

  async function handleDeletePraxis() {
    if (!isAdmin) {
      alert('Du hast keine Berechtigung, eine Praxis zu löschen.');
      return;
    }
    const filialenCount = await fetchFilialenCount(pb, praxis.id);
    if (filialenCount > 0) {
      alert(
        `Diese Praxis hat ${filialenCount} ${filialenCount === 1 ? 'Filiale' : 'Filialen'}. ` +
          `Bitte erst die Filialen entfernen oder von der Mutter trennen.`
      );
      return;
    }
    if (
      !window.confirm(
        `"${praxis.name}" wirklich löschen? Alle zugeordneten Kontakte, Aktivitäten und Dokumente werden ebenfalls entfernt.`
      )
    ) {
      return;
    }
    try {
      await pb.collection('praxen').delete(praxis.id);
      navigate('/praxen', { replace: true });
    } catch (err) {
      if (err?.status === 403) {
        alert('Du hast keine Berechtigung, diesen Datensatz zu löschen.');
      } else {
        alert(err?.message || 'Löschen fehlgeschlagen.');
      }
    }
  }

  function openCreateKontakt() {
    setEditKontakt(null);
    setKontaktModalOpen(true);
  }

  function openEditKontakt(kontakt) {
    setEditKontakt(kontakt);
    setKontaktModalOpen(true);
  }

  async function handleDeleteKontakt(kontakt) {
    if (!isFuehrungskraftOrAdmin) {
      alert('Du hast keine Berechtigung, einen Kontakt zu löschen.');
      return;
    }
    // Der Hinweis auf die Aktivitäten ist kein Beiwerk: aktivitaeten.kontakt
    // ist cascadeDelete=false, die Aktivitäten bleiben also stehen und
    // verlieren still ihren Kontaktbezug. Wer löscht, soll das vorher wissen.
    if (
      !window.confirm(
        `Kontakt "${formatKontaktName(kontakt)}" wirklich löschen?\n\n` +
          'Aktivitäten mit diesem Kontakt bleiben erhalten, verlieren aber den Kontaktbezug.'
      )
    ) {
      return;
    }
    try {
      await pb.collection('kontakte').delete(kontakt.id);
      setKontakte((prev) => prev.filter((k) => k.id !== kontakt.id));
    } catch (err) {
      if (err?.status === 403) {
        alert('Du hast keine Berechtigung, diesen Datensatz zu löschen.');
      } else {
        alert(err?.message || 'Löschen fehlgeschlagen.');
      }
    }
  }

  // Power-Dialer-Hook: wenn die Praxis im Kontext einer Selektion geöffnet
  // wurde, hängen wir an Aktivitäts-Saves einen Erledigt-Mark + Redirect an.
  // Edits triggern bewusst keine Erledigt-Logik — nur neue Anrufe zählen.
  async function powerDialerOnSaved(savedAktivitaet) {
    if (!selektionId) return;
    try {
      let bereitsErledigt = false;
      try {
        await pb
          .collection('selektion_erledigt')
          .getFirstListItem(
            `selektion = "${selektionId}" && praxis = "${id}"`,
            { fields: 'id' }
          );
        bereitsErledigt = true;
      } catch (err) {
        // 404 = noch nicht erledigt → wir legen den Mark an. Andere Fehler
        // schlucken wir bewusst nicht — sie sollen in den catch fallen.
        if (err?.status !== 404) throw err;
      }
      if (!bereitsErledigt && savedAktivitaet?.id) {
        await pb.collection('selektion_erledigt').create({
          selektion: selektionId,
          praxis: id,
          erledigt_von: user?.id,
          erledigt_am: heuteAlsInputValue(),
          aktivitaet: savedAktivitaet.id,
        });
      }
      navigate(`/selektionen/${selektionId}?erledigt=${id}`);
    } catch {
      // Erledigt-Mark hat nicht geklappt (z. B. Race auf den Unique-Index).
      // Wir springen trotzdem zur Selektion zurück, aber ohne Toast-Param,
      // damit der User nicht "erledigt"-Feedback bekommt, ohne dass eine
      // Mark gesetzt wurde.
      navigate(`/selektionen/${selektionId}`);
    }
  }

  function openCreateAktivitaet() {
    aktivitaetModal.openCreate({
      praxisId: id,
      kontakte,
      onSaved: powerDialerOnSaved,
    });
  }

  function openCreateAktivitaetForKontakt(kontaktId) {
    aktivitaetModal.openCreate({
      praxisId: id,
      kontakte,
      prefill: { kontakt: kontaktId },
      onSaved: powerDialerOnSaved,
    });
  }

  function openEditAktivitaet(aktivitaet) {
    aktivitaetModal.openEdit({ aktivitaet, kontakte });
  }

  function openDuplicateAktivitaet(aktivitaet) {
    // Alle Felder kopieren, Status zurück auf "offen", erstellt_von und id
    // werden beim Save automatisch neu gesetzt. Wiedervorlage wird bewusst
    // NICHT übernommen, weil sie typisch zur konkreten Vorgängeraufgabe gehört.
    aktivitaetModal.openCreate({
      praxisId: id,
      kontakte,
      prefill: {
        typ: aktivitaet.typ,
        titel: aktivitaet.titel,
        beschreibung: aktivitaet.beschreibung || '',
        faellig_am: toDateInputValue(aktivitaet.faellig_am),
        uhrzeit: aktivitaet.uhrzeit || '',
        kontakt: aktivitaet.kontakt || '',
        zustaendig: aktivitaet.zustaendig || '',
        // status: NICHT vorbelegt → EMPTY-Default 'offen' bleibt
      },
      onSaved: powerDialerOnSaved,
    });
  }

  async function handleDeleteAktivitaet(aktivitaet) {
    const darfLoeschen = isAdmin || aktivitaet?.erstellt_von === user?.id;
    if (!darfLoeschen) {
      alert('Du kannst nur eigene Aktivitäten löschen.');
      return;
    }
    if (!window.confirm(`Aktivität "${aktivitaet.titel}" wirklich löschen?`)) return;
    try {
      await pb.collection('aktivitaeten').delete(aktivitaet.id);
      setAktivitaeten((prev) => prev.filter((a) => a.id !== aktivitaet.id));
    } catch (err) {
      if (err?.status === 403) {
        alert('Du hast keine Berechtigung, diesen Datensatz zu löschen.');
      } else {
        alert(err?.message || 'Löschen fehlgeschlagen.');
      }
    }
  }

  async function handleStatusChange(aktivitaet, nextStatus) {
    try {
      const updated = await pb
        .collection('aktivitaeten')
        .update(aktivitaet.id, { status: nextStatus }, { expand: 'zustaendig,kontakt,erstellt_von' });
      setAktivitaeten((prev) => prev.map((a) => (a.id === aktivitaet.id ? updated : a)));
    } catch (err) {
      alert(err?.message || 'Status konnte nicht aktualisiert werden.');
    }
  }

  async function handleDeleteDokument(dokument) {
    if (!window.confirm(`Dokument "${dokument.titel}" wirklich löschen?`)) return;
    try {
      await pb.collection('dokumente').delete(dokument.id);
      setDokumente((prev) => prev.filter((d) => d.id !== dokument.id));
    } catch (err) {
      alert(err?.message || 'Löschen fehlgeschlagen.');
    }
  }

  if (loading) {
    return <div className="text-sm text-slate-500">Lade …</div>;
  }

  if (error || !praxis) {
    return (
      <div className="space-y-4">
        <Link to="/praxen" className="text-sm text-primary-700 hover:underline">
          ← zurück zur Liste
        </Link>
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 ring-1 ring-red-200">
          {error || 'Praxis nicht gefunden.'}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {selektionKontext && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-md bg-amber-50 px-4 py-3 ring-1 ring-amber-200">
          <div className="text-sm text-amber-900">
            Du arbeitest in der Selektion:{' '}
            <span className="font-semibold">{selektionKontext.name}</span>
          </div>
          <button
            type="button"
            className="text-sm font-medium text-amber-900 hover:text-amber-700"
            onClick={() => navigate(`/selektionen/${selektionKontext.id}`)}
          >
            ← Zurück zur Selektion
          </button>
        </div>
      )}

      {praxis.expand?.mutter_praxis && (
        <button
          type="button"
          onClick={() => navigate(`/praxen/${praxis.expand.mutter_praxis.id}`)}
          className="flex w-full flex-wrap items-center justify-between gap-3 rounded-md bg-sky-50 px-4 py-3 text-left ring-1 ring-sky-200 hover:bg-sky-100"
        >
          <div className="text-sm text-sky-900">
            <span aria-hidden="true">ⓘ</span> Filiale von{' '}
            <span className="font-semibold">{praxis.expand.mutter_praxis.name}</span>
          </div>
          <span className="text-sm font-medium text-sky-900">Zur Hauptpraxis →</span>
        </button>
      )}

      {filialen.length > 0 && (
        <button
          type="button"
          onClick={() => setTab('filialen')}
          className="flex w-full flex-wrap items-center justify-between gap-3 rounded-md bg-sky-50 px-4 py-3 text-left ring-1 ring-sky-200 hover:bg-sky-100"
        >
          <div className="text-sm text-sky-900">
            <span aria-hidden="true">ⓘ</span> Hat{' '}
            <span className="font-semibold">
              {filialen.length} {filialen.length === 1 ? 'Filiale' : 'Filialen'}
            </span>
          </div>
          <span className="text-sm font-medium text-sky-900">Alle Filialen ansehen →</span>
        </button>
      )}

      <Link to="/praxen" className="text-sm text-primary-700 hover:underline">
        ← zurück zur Liste
      </Link>

      <div className="card space-y-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-900">{praxisAnzeige(praxis)}</h1>
              <StatusPill status={praxis.status} />
              <PraxisArtPill praxisArt={praxis.praxis_art} />
              <UnternehmensformPill unternehmensform={praxis.unternehmensform} />
              <VersorgungsformPill versorgungsform={praxis.versorgungsform} />
              <VerbundPill praxis={praxis} />
            </div>
            <p className="mt-1 text-sm text-slate-500">
              {TYP_LABEL_PRAXIS[praxis.typ] ?? praxis.typ}
            </p>
            <div className="mt-2">
              <AnrufAmpel praxis={praxis} showText />
            </div>
            {isFuehrungskraftOrAdmin && praxis.expand?.partner && (
              <p className="mt-1 text-sm text-slate-500">
                Empfohlen durch{' '}
                <Link
                  to="/partner"
                  className="font-medium text-primary-700 hover:underline"
                >
                  {praxis.expand.partner.name}
                </Link>
              </p>
            )}
            <p className="mt-3 text-xs text-slate-400">
              {praxis.expand?.angelegt_von ? (
                <>
                  Angelegt von {userAnzeige(praxis.expand.angelegt_von)} am{' '}
                  {formatDatum(praxis.created)}
                </>
              ) : (
                <>Angelegt am {formatDatum(praxis.created)}</>
              )}
            </p>
          </div>
          <div className="flex gap-2">
            <button type="button" className="btn-secondary" onClick={() => setPraxisModalOpen(true)}>
              Bearbeiten
            </button>
            {isAdmin && (
              <button type="button" className="btn-danger" onClick={handleDeletePraxis}>
                Löschen
              </button>
            )}
          </div>
        </div>

        <StammdatenBlock praxis={praxis} />
      </div>

      <VertragCard praxis={praxis} onEdit={() => setVertragModalOpen(true)} />

      <div className="card">
        <div className="border-b border-slate-200">
          <nav className="-mb-px flex gap-6">
            <TabButton active={tab === 'kontakte'} onClick={() => setTab('kontakte')} badge={kontakte.length}>
              Kontakte
            </TabButton>
            <TabButton
              active={tab === 'aktivitaeten'}
              onClick={() => setTab('aktivitaeten')}
              badge={aktivitaeten.length}
            >
              Aktivitäten
            </TabButton>
            <TabButton
              active={tab === 'dokumente'}
              onClick={() => setTab('dokumente')}
              badge={dokumente.length}
            >
              Dokumente
            </TabButton>
            {filialen.length > 0 && (
              <TabButton
                active={tab === 'filialen'}
                onClick={() => setTab('filialen')}
                badge={filialen.length}
              >
                Alle Filialen
              </TabButton>
            )}
          </nav>
        </div>

        {tab === 'kontakte' && (
          <div className="pt-5">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-slate-500">
                {kontakte.length} Person{kontakte.length === 1 ? '' : 'en'} dieser{' '}
                {TYP_LABEL_PRAXIS[praxis.typ] ?? 'Organisation'}
              </p>
              <button type="button" className="btn-primary" onClick={openCreateKontakt}>
                + Kontakt hinzufügen
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Name
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Rolle
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      E-Mail
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Telefon
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Angelegt von
                    </th>
                    <th className="px-4 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 bg-white">
                  {kontakte.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-6 text-center text-sm text-slate-500">
                        Noch keine Kontakte angelegt.
                      </td>
                    </tr>
                  )}
                  {kontakte.map((kontakt) => (
                    <tr key={kontakt.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">
                          {formatKontaktName(kontakt)}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {rolleAnzeige(kontakt.rolle)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {kontakt.email ? (
                          <a
                            href={`mailto:${kontakt.email}`}
                            className="text-primary-700 hover:underline"
                          >
                            {kontakt.email}
                          </a>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        <TelefonLink
                          phone={kontakt.mobil || kontakt.telefon}
                          praxisId={praxis.id}
                          kontaktId={kontakt.id}
                          kontakte={kontakte}
                        />
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {kontakt.expand?.angelegt_von ? (
                          <>
                            <div className="font-medium text-slate-700">
                              {userAnzeige(kontakt.expand.angelegt_von)}
                            </div>
                            <div className="text-xs text-slate-500">
                              {formatDatum(kontakt.created)}
                            </div>
                          </>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-3">
                          <button
                            type="button"
                            className="text-sm font-medium text-primary-600 hover:text-primary-800"
                            onClick={() => openCreateAktivitaetForKontakt(kontakt.id)}
                            title="Aktivität für diesen Kontakt anlegen"
                          >
                            + Aktivität
                          </button>
                          <button
                            type="button"
                            className="text-sm font-medium text-primary-600 hover:text-primary-800"
                            onClick={() => openEditKontakt(kontakt)}
                          >
                            Bearbeiten
                          </button>
                          {isFuehrungskraftOrAdmin && (
                            <button
                              type="button"
                              className="text-sm font-medium text-red-600 hover:text-red-800"
                              onClick={() => handleDeleteKontakt(kontakt)}
                            >
                              Löschen
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'aktivitaeten' && (
          <div className="pt-5">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-slate-500">
                {aktivitaeten.length} Aktivität
                {aktivitaeten.length === 1 ? '' : 'en'} insgesamt
              </p>
              <button type="button" className="btn-primary" onClick={openCreateAktivitaet}>
                + Neue Aktivität
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Typ
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Titel
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Status
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Fällig
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Zuständig
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Angelegt von
                    </th>
                    <th className="px-4 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 bg-white">
                  {aktivitaeten.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-4 py-6 text-center text-sm text-slate-500">
                        Noch keine Aktivitäten angelegt.
                      </td>
                    </tr>
                  )}
                  {(() => {
                    // IDs der aktuell geladenen Aktivitäten, damit wir den
                    // "Folgt auf"-Link nur dann als Scroll-Link rendern, wenn
                    // die Vorgänger-Zeile auch wirklich im DOM ist.
                    const aktivitaetenIds = new Set(aktivitaeten.map((a) => a.id));
                    return aktivitaeten.map((aktivitaet) => {
                      const ueberfaellig = istUeberfaellig(aktivitaet.faellig_am, aktivitaet.status);
                      const beschreibungText = (aktivitaet.beschreibung || '').trim();
                      const vorgaenger = aktivitaet.expand?.vorgaenger_aktivitaet;
                      const vorgaengerInListe =
                        vorgaenger && aktivitaetenIds.has(vorgaenger.id);
                      return (
                      <tr
                        key={aktivitaet.id}
                        id={`aktivitaet-${aktivitaet.id}`}
                        className="hover:bg-slate-50"
                      >
                        <td className="px-4 py-3 text-sm text-slate-700">
                          <span title={TYP_LABEL[aktivitaet.typ]}>
                            {TYP_ICON[aktivitaet.typ] ?? '•'} {TYP_LABEL[aktivitaet.typ] ?? aktivitaet.typ}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-900">{aktivitaet.titel}</div>
                          {aktivitaet.ist_wiedervorlage && vorgaenger && (
                            <div className="mt-0.5 text-xs text-slate-500">
                              Folgt auf:{' '}
                              {vorgaengerInListe ? (
                                <button
                                  type="button"
                                  onClick={() => {
                                    const el = document.getElementById(
                                      `aktivitaet-${vorgaenger.id}`
                                    );
                                    el?.scrollIntoView({
                                      behavior: 'smooth',
                                      block: 'center',
                                    });
                                  }}
                                  className="text-primary-700 hover:underline"
                                >
                                  {vorgaenger.titel}
                                </button>
                              ) : (
                                <span>{vorgaenger.titel}</span>
                              )}
                            </div>
                          )}
                          <NotizInline text={beschreibungText} />
                          {aktivitaet.typ === 'telefonat' && aktivitaet.gespraech_ergebnis && (
                            <div className="mt-1">
                              <span
                                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${
                                  GESPRAECH_ERGEBNIS_BADGE_CLASS[aktivitaet.gespraech_ergebnis] ??
                                  'bg-slate-100 text-slate-700 ring-slate-200'
                                }`}
                              >
                                {GESPRAECH_ERGEBNIS_SHORT_LABEL[aktivitaet.gespraech_ergebnis] ??
                                  aktivitaet.gespraech_ergebnis}
                              </span>
                            </div>
                          )}
                          {aktivitaet.expand?.kontakt && (
                            <div className="text-xs text-slate-500">
                              mit {formatKontaktName(aktivitaet.expand.kontakt)}
                            </div>
                          )}
                          {(aktivitaet.ist_wiedervorlage || aktivitaet.wiedervorlage_am) && (
                            <div className="mt-1">
                              <WiedervorlageMarkierung
                                ist_wiedervorlage={aktivitaet.ist_wiedervorlage}
                                wiedervorlage_am={aktivitaet.wiedervorlage_am}
                                variant="both"
                              />
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <AktivitaetStatusBadge
                            status={aktivitaet.status}
                            onChange={(next) => handleStatusChange(aktivitaet, next)}
                          />
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span className={ueberfaellig ? 'font-medium text-red-600' : 'text-slate-600'}>
                            {formatDatumZeit(aktivitaet.faellig_am, aktivitaet.uhrzeit)}
                            {ueberfaellig && ' (überfällig)'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {userAnzeige(aktivitaet.expand?.zustaendig)}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {aktivitaet.expand?.erstellt_von ? (
                            <>
                              <div className="font-medium text-slate-700">
                                {userAnzeige(aktivitaet.expand.erstellt_von)}
                              </div>
                              <div className="text-xs text-slate-500">
                                {formatDatumZeitVoll(aktivitaet.created)}
                              </div>
                            </>
                          ) : (
                            <span className="text-slate-500">
                              {formatDatumZeitVoll(aktivitaet.created)}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-3">
                            <button
                              type="button"
                              className="text-sm font-medium text-primary-600 hover:text-primary-800"
                              onClick={() => openEditAktivitaet(aktivitaet)}
                            >
                              Bearbeiten
                            </button>
                            <button
                              type="button"
                              className="text-sm font-medium text-primary-600 hover:text-primary-800"
                              onClick={() => openDuplicateAktivitaet(aktivitaet)}
                              title="Aktivität als neue offene Aufgabe kopieren"
                            >
                              Duplizieren
                            </button>
                            {(isAdmin || aktivitaet.erstellt_von === user?.id) && (
                              <button
                                type="button"
                                className="text-sm font-medium text-red-600 hover:text-red-800"
                                onClick={() => handleDeleteAktivitaet(aktivitaet)}
                              >
                                Löschen
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                    });
                  })()}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'dokumente' && (
          <div className="pt-5">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-slate-500">
                {dokumente.length} Dokument{dokumente.length === 1 ? '' : 'e'} hochgeladen
              </p>
              <button type="button" className="btn-primary" onClick={() => setDokumentModalOpen(true)}>
                + Dokument hochladen
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Titel
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Typ
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Datei
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Größe
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Hochgeladen
                    </th>
                    <th className="px-4 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 bg-white">
                  {dokumente.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-6 text-center text-sm text-slate-500">
                        Noch keine Dokumente hochgeladen.
                      </td>
                    </tr>
                  )}
                  {dokumente.map((dok) => {
                    const url = dok.datei ? pb.files.getUrl(dok, dok.datei, { download: true }) : null;
                    const ext = dateiendung(dok.dateiname || dok.datei || '');
                    return (
                      <tr key={dok.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-900">{dok.titel}</div>
                          {dok.notiz && (
                            <div className="line-clamp-1 text-xs text-slate-500">
                              {dok.notiz.replace(/<[^>]+>/g, '')}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${
                              DOKUMENT_TYP_BADGE[dok.typ] ?? 'bg-slate-100 text-slate-700 ring-slate-200'
                            }`}
                          >
                            {DOKUMENT_TYP_LABEL[dok.typ] ?? dok.typ}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          <span className="font-mono text-xs">{ext || '—'}</span>
                          {dok.dateiname && (
                            <div className="text-xs text-slate-500">{dok.dateiname}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {formatDateigroesse(dok.dateigroesse) || '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {formatDatum(dok.created)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-2">
                            {url && (
                              <a
                                href={url}
                                target="_blank"
                                rel="noreferrer"
                                className="text-sm font-medium text-primary-600 hover:text-primary-800"
                              >
                                Download
                              </a>
                            )}
                            <button
                              type="button"
                              className="text-sm font-medium text-red-600 hover:text-red-800"
                              onClick={() => handleDeleteDokument(dok)}
                            >
                              Löschen
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'filialen' && (
          <FilialenTab
            hauptpraxis={praxis}
            filialen={filialen}
            aktivitaeten={filialenAktivitaeten}
            kontakte={filialenKontakte}
            loading={filialenAggregLoading}
            onNavigate={(praxisId) => navigate(`/praxen/${praxisId}`)}
          />
        )}
      </div>

      {praxis.notizen?.trim() && (
        <div className="card space-y-2">
          <div>
            <h3 className="text-sm font-semibold text-slate-700">Notizen (alt)</h3>
            <p className="mt-0.5 text-xs text-slate-500">
              Dieses Feld wird nicht mehr verwendet. Notizen bitte als Aktivität
              erfassen.
            </p>
          </div>
          <textarea
            readOnly
            rows={3}
            className="input cursor-not-allowed bg-slate-50 text-slate-500"
            value={praxis.notizen}
          />
        </div>
      )}

      <PraxisFormModal
        open={praxisModalOpen}
        onClose={() => setPraxisModalOpen(false)}
        praxis={praxis}
        onSaved={() => load()}
      />
      <KontaktFormModal
        open={kontaktModalOpen}
        onClose={() => setKontaktModalOpen(false)}
        praxisId={praxis.id}
        kontakt={editKontakt}
        onSaved={() => load()}
      />
      <DokumentFormModal
        open={dokumentModalOpen}
        onClose={() => setDokumentModalOpen(false)}
        praxisId={praxis.id}
        onSaved={() => load()}
      />
      <VertragFormModal
        open={vertragModalOpen}
        onClose={() => setVertragModalOpen(false)}
        praxis={praxis}
        onSaved={() => load()}
      />
    </div>
  );
}
